import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  useTheme,
} from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import { listPricelists, listUsers } from '../../graphql/queries';
import { updateUser } from '../../graphql/mutations';
import { AppContext } from '../../App';
import { useUser } from '../../UserContext';
import {
  getCustomerHierarchy,
  getCustomerOffsets,
  createDerivedPricelist,
  createPricelistUpdateNotification,
} from '../../utils/pricelistPushUtils';
import InnerPage from '../InnerPage';

const PushPricelist = () => {
  const { userRecord } = useContext(AppContext);
  const { userClass } = useUser();
  const theme = useTheme();
  
  const [pricelists, setPricelists] = useState([]);
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [passThroughToSubCustomers, setPassThroughToSubCustomers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });

  useEffect(() => {
    // Only fetch data if user is SuperAdmin
    if (userClass === 'SuperAdmin' && userRecord) {
      fetchPricelists();
      fetchCustomers();
    }
  }, [userRecord, userClass]);

  const fetchPricelists = async () => {
    try {
      // Fetch pricelists owned by the user (userId)
      const ownedResponse = await API.graphql(
        graphqlOperation(listPricelists, {
          filter: {
            userId: { eq: userRecord.id }
          },
          limit: 1000
        })
      );
      
      const ownedPricelists = ownedResponse.data.listPricelists.items || [];
      
      // Fetch all pricelists and filter client-side for providerId
      // Note: providerId is not available as a filter field in GraphQL schema
      const allResponse = await API.graphql(
        graphqlOperation(listPricelists, {
          limit: 1000
        })
      );
      
      const allPricelists = allResponse.data.listPricelists.items || [];
      
      // Filter for pricelists where user is the provider (client-side filter)
      const providedPricelists = allPricelists.filter(
        p => p && p.providerId === userRecord.id
      );
      
      // Combine and deduplicate by ID
      const combinedPricelists = [...ownedPricelists, ...providedPricelists];
      const uniquePricelistsMap = new Map();
      
      combinedPricelists.forEach(pricelist => {
        if (pricelist && pricelist.id && !uniquePricelistsMap.has(pricelist.id)) {
          uniquePricelistsMap.set(pricelist.id, pricelist);
        }
      });
      
      const pricelistsData = Array.from(uniquePricelistsMap.values());
      
      setPricelists(pricelistsData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      ));
    } catch (error) {
      console.error('Error fetching pricelists:', error);
      showSnackbar('Error fetching pricelists', 'error');
    }
  };

  const fetchCustomers = async () => {
    try {
      const hierarchy = await getCustomerHierarchy(userRecord.id);
      setCustomers(hierarchy);
    } catch (error) {
      console.error('Error fetching customers:', error);
      showSnackbar('Error fetching customers', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCustomerToggle = (customerId) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
    } else {
      newSelected.add(customerId);
    }
    setSelectedCustomers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCustomers.size === customers.length) {
      setSelectedCustomers(new Set());
    } else {
      setSelectedCustomers(new Set(customers.map(c => c.id)));
    }
  };

  const handlePushPricelist = async () => {
    if (!selectedPricelist) {
      showSnackbar('Please select a pricelist to push', 'error');
      return;
    }

    if (selectedCustomers.size === 0) {
      showSnackbar('Please select at least one customer', 'error');
      return;
    }

    setConfirmDialogOpen(true);
  };

  const confirmPush = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);
    
    try {
      // Get the full pricelist details
      const pricelistResponse = await API.graphql(
        graphqlOperation(/* GraphQL */ `
          query GetPricelist($id: ID!) {
            getPricelist(id: $id) {
              id
              userId
              validRows
              dataCount
              s3Key
              filename
              name
              adjustments
              status
            }
          }
        `, { id: selectedPricelist.id })
      );
      
      const sourcePricelist = pricelistResponse.data.getPricelist;
      
      if (!sourcePricelist) {
        throw new Error('Pricelist not found');
      }

      const selectedCustomerIds = Array.from(selectedCustomers);
      const customersToProcess = customers.filter(c => selectedCustomerIds.includes(c.id));
      
      // Build a map of customer hierarchies for offset stacking
      const customerMap = new Map();
      customers.forEach(c => {
        customerMap.set(c.id, c);
      });
      
      // Function to get offset chain for a customer (including parent offsets)
      const getOffsetChain = (customerId) => {
        const chain = [];
        let currentId = customerId;
        const visited = new Set();
        
        while (currentId && !visited.has(currentId)) {
          visited.add(currentId);
          const customer = customerMap.get(currentId);
          if (customer) {
            chain.push(customer);
            currentId = customer.parentId;
          } else {
            break;
          }
        }
        
        return chain.reverse(); // Reverse to get from root to customer
      };

      setProgress({ current: 0, total: customersToProcess.length, message: 'Starting push...' });

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < customersToProcess.length; i++) {
        const customer = customersToProcess[i];
        
        try {
          setProgress({ 
            current: i + 1, 
            total: customersToProcess.length, 
            message: `Processing ${customer.username || customer.email}...` 
          });

          // Get offset chain for this customer
          const offsetChain = getOffsetChain(customer.id);
          
          // Get offsets for each level in the chain
          const offsetLevels = [];
          for (const chainCustomer of offsetChain) {
            const offsets = await getCustomerOffsets(chainCustomer);
            if (offsets.percentageAdjustment !== 0 || offsets.dollarAdjustment !== 0) {
              offsetLevels.push(offsets);
            }
          }

          // Create derived pricelist with offsets applied
          const derivedPricelist = await createDerivedPricelist(
            sourcePricelist,
            customer,
            offsetLevels,
            userRecord.id
          );

          // Assign pricelist to customer (make it their active pricelist)
          await API.graphql(
            graphqlOperation(updateUser, {
              input: {
                id: customer.id,
                activePricelistID: derivedPricelist.id
              }
            })
          );

          // Create notification
          await createPricelistUpdateNotification(
            customer.id,
            sourcePricelist.name,
            userRecord.id
          );

          successCount++;

          // If pass through is enabled, process sub-customers
          if (passThroughToSubCustomers) {
            const subCustomers = customers.filter(c => c.parentId === customer.id);
            
            for (const subCustomer of subCustomers) {
              try {
                setProgress({ 
                  current: i + 1, 
                  total: customersToProcess.length, 
                  message: `Processing sub-customer ${subCustomer.username || subCustomer.email}...` 
                });

                // Get offset chain including parent customer
                const subOffsetChain = getOffsetChain(subCustomer.id);
                const subOffsetLevels = [];
                
                for (const chainCustomer of subOffsetChain) {
                  const offsets = await getCustomerOffsets(chainCustomer);
                  if (offsets.percentageAdjustment !== 0 || offsets.dollarAdjustment !== 0) {
                    subOffsetLevels.push(offsets);
                  }
                }

                // Create derived pricelist for sub-customer
                const subDerivedPricelist = await createDerivedPricelist(
                  sourcePricelist,
                  subCustomer,
                  subOffsetLevels,
                  userRecord.id
                );

                // Assign and make default
                await API.graphql(
                  graphqlOperation(updateUser, {
                    input: {
                      id: subCustomer.id,
                      activePricelistID: subDerivedPricelist.id
                    }
                  })
                );

                // Create notification for sub-customer
                await createPricelistUpdateNotification(
                  subCustomer.id,
                  sourcePricelist.name,
                  userRecord.id
                );

                successCount++;
              } catch (error) {
                console.error(`Error processing sub-customer ${subCustomer.id}:`, error);
                errorCount++;
              }
            }
          }
        } catch (error) {
          console.error(`Error processing customer ${customer.id}:`, error);
          errorCount++;
        }
      }

      setLoading(false);
      showSnackbar(
        `Pricelist push completed! Success: ${successCount}, Errors: ${errorCount}`,
        errorCount > 0 ? 'warning' : 'success'
      );
      
      // Reset selections
      setSelectedCustomers(new Set());
      setSelectedPricelist(null);
      setPassThroughToSubCustomers(false);
      setProgress({ current: 0, total: 0, message: '' });
    } catch (error) {
      console.error('Error pushing pricelist:', error);
      setLoading(false);
      showSnackbar('Error pushing pricelist: ' + error.message, 'error');
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  const getOffsetDisplay = (customer) => {
    if (customer.userGroup?.percentageAdjustment || customer.userGroup?.dollarAdjustment) {
      const pct = customer.userGroup.percentageAdjustment || 0;
      const dollar = customer.userGroup.dollarAdjustment || 0;
      return `${pct ? pct + '%' : ''}${pct && dollar ? ' + ' : ''}${dollar ? '$' + dollar : ''}`;
    }
    if (customer.priceOffsetType && customer.priceOffsetValue) {
      if (customer.priceOffsetType === 'percentage') {
        return `${customer.priceOffsetValue}%`;
      } else {
        return `$${customer.priceOffsetValue}`;
      }
    }
    return 'None';
  };

  // Only allow SuperAdmin access
  if (userClass !== 'SuperAdmin') {
    return (
      <InnerPage>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">This feature is only available to SuperAdmins.</Alert>
        </Box>
      </InnerPage>
    );
  }

  return (
    <InnerPage>
      <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Push Pricelist to Customers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a pricelist and customers to push it to. Customer offsets will be automatically applied.
      </Typography>

      {/* Pricelist Selection */}
      <Card sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Select Pricelist</InputLabel>
          <Select
            value={selectedPricelist?.id || ''}
            onChange={(e) => {
              const pricelist = pricelists.find(p => p.id === e.target.value);
              setSelectedPricelist(pricelist || null);
            }}
            label="Select Pricelist"
          >
            {pricelists.map((pricelist) => (
              <MenuItem key={pricelist.id} value={pricelist.id}>
                {pricelist.name} ({pricelist.validRows} parts)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>

      {/* Pass Through Toggle */}
      <Card sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={passThroughToSubCustomers}
              onChange={(e) => setPassThroughToSubCustomers(e.target.checked)}
            />
          }
          label="Pass pricelist through to sub-customers (automatically set as default for customers' customers)"
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
          When enabled, the pricelist will be automatically set as the default pricelist for your customers' customers, 
          with offsets applied at each level.
        </Typography>
      </Card>

      {/* Customers List */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Select Customers ({selectedCustomers.size} selected)
          </Typography>
          <Button size="small" onClick={handleSelectAll}>
            {selectedCustomers.size === customers.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {customers.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No customers found. Customers will appear here once they are created.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedCustomers.size === customers.length && customers.length > 0}
                      indeterminate={selectedCustomers.size > 0 && selectedCustomers.size < customers.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Offsets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCustomers.has(customer.id)}
                        onChange={() => handleCustomerToggle(customer.id)}
                      />
                    </TableCell>
                    <TableCell>{customer.username || 'N/A'}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={`Level ${customer.depth + 1}`} 
                        size="small" 
                        color={customer.depth === 0 ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>{getOffsetDisplay(customer)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Push Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePushPricelist}
          disabled={loading || !selectedPricelist || selectedCustomers.size === 0}
          size="large"
        >
          Push Pricelist
        </Button>
      </Box>

      {/* Progress Dialog */}
      {loading && (
        <Dialog open={loading} onClose={() => {}}>
          <DialogTitle>Pushing Pricelist</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                {progress.message}
              </Typography>
              {progress.total > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {progress.current} of {progress.total} customers processed
                </Typography>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Pricelist Push</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to push the pricelist <strong>{selectedPricelist?.name}</strong> to{' '}
            <strong>{selectedCustomers.size}</strong> customer{selectedCustomers.size !== 1 ? 's' : ''}.
          </Typography>
          {passThroughToSubCustomers && (
            <Typography sx={{ mt: 2, color: 'warning.main' }}>
              <strong>Note:</strong> This will also automatically set the pricelist as default for all sub-customers 
              with offsets applied at each level.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmPush} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </InnerPage>
  );
};

export default PushPricelist;

